import React, { useState } from "react";
import Icon from "../../../../components/Icon";
import Modal from "../../../../components/Modal";
import {
  addSubnet,
  testRelayConnection,
  bootstrapSubnet,
} from "../../services/DiscoveryService";

export function SubnetRelayWizardModal({ isOpen, onClose, onSuccess }) {
  const [wizardStep, setWizardStep] = useState(1);
  const [isInstallingRelay, setIsInstallingRelay] = useState(false);
  const [bootstrapFailed, setBootstrapFailed] = useState(false);
  const [bootstrapSuccess, setBootstrapSuccess] = useState(false);
  const [isTestingSSH, setIsTestingSSH] = useState(false);
  const [sshTestError, setSshTestError] = useState(null);
  const [sshTestSuccess, setSshTestSuccess] = useState(false);
  const [subnetFormError, setSubnetFormError] = useState(null);
  const [showRelayKey, setShowRelayKey] = useState(false);
  const [installLogs, setInstallLogs] = useState([]);

  const defaultKafka =
    process.env.REACT_APP_KAFKA_BROKER || "10.128.15.205:9092";

  const [newSubnetForm, setNewSubnetForm] = useState({
    subnet: "",
    name: "",
    floor: "",
    snmp_community: "public",
    snmp_port: 161,
    relay_ip: "",
    ssh_port: 22,
    ssh_user: "ubuntu",
    ssh_pkey: "",
    scan_interval_secs: 300,
    kafka_broker: defaultKafka,
    authorized: false,
  });

  if (!isOpen) return null;

  const handleClose = () => {
    if (!isInstallingRelay || bootstrapFailed || bootstrapSuccess) {
      setWizardStep(1);
      setBootstrapFailed(false);
      setBootstrapSuccess(false);
      setIsInstallingRelay(false);
      setSshTestError(null);
      setSshTestSuccess(false);
      setSubnetFormError(null);
      setShowRelayKey(false);
      onClose();
    }
  };

  const handleTestRelaySSH = async () => {
    setSshTestError(null);
    setSshTestSuccess(false);

    const ip = (newSubnetForm.relay_ip || "").trim();
    if (!ip) {
      setSshTestError("Relay VM IP Address is required.");
      return;
    }
    const ipRegex =
      /^((25[0-5]|2[0-4][0-9]|[01]?[0-9][0-9]?)\.){3}(25[0-5]|2[0-4][0-9]|[01]?[0-9][0-9]?)$/;
    if (!ipRegex.test(ip)) {
      setSshTestError("Please enter a valid IPv4 address (e.g. 10.30.5.5).");
      return;
    }

    const portNum = parseInt(newSubnetForm.ssh_port, 10);
    if (isNaN(portNum) || portNum < 1 || portNum > 65535) {
      setSshTestError("SSH Port must be a valid port number between 1 and 65535.");
      return;
    }

    const user = (newSubnetForm.ssh_user || "").trim();
    if (!user) {
      setSshTestError("SSH Username is required.");
      return;
    }

    const pkey = (newSubnetForm.ssh_pkey || "").trim();
    if (!pkey) {
      setSshTestError("SSH Private Key is required.");
      return;
    }
    if (!pkey.includes("-----BEGIN") || !pkey.includes("-----END")) {
      setSshTestError(
        "Invalid key structure: SSH Private Key must be in PEM format (must start with '-----BEGIN' and end with '-----END').",
      );
      return;
    }

    setIsTestingSSH(true);

    try {
      const response = await testRelayConnection({
        relay_ip: ip,
        ssh_port: portNum,
        ssh_user: user,
        ssh_pkey: pkey,
      });

      if (response && response.success) {
        setSshTestSuccess(true);
        await new Promise((resolve) => setTimeout(resolve, 1000));
        setIsTestingSSH(false);
        setSshTestSuccess(false);
        setWizardStep(2);
      } else {
        setSshTestError(
          response?.error ||
            "SSH connection check failed. Please verify that the Relay VM IP, Port, and Username are correct.",
        );
        setIsTestingSSH(false);
      }
    } catch (err) {
      console.error(err);
      setSshTestError(
        "Verification failed: The auto discovery backend services are unreachable. Please ensure the AIOps API server is running and database migrations have been successfully applied.",
      );
      setIsTestingSSH(false);
    }
  };

  const handleSaveSubnet = async () => {
    setSubnetFormError(null);

    const subnet = (newSubnetForm.subnet || "").trim();
    if (!subnet) {
      setSubnetFormError("Subnet CIDR range is required.");
      return;
    }
    const cidrRegex =
      /^((25[0-5]|2[0-4][0-9]|[01]?[0-9][0-9]?)\.){3}(25[0-5]|2[0-4][0-9]|[01]?[0-9][0-9]?)\/(3[0-2]|[12]?[0-9])$/;
    if (!cidrRegex.test(subnet)) {
      setSubnetFormError(
        "Please enter a valid IPv4 CIDR range structure (e.g. 10.30.5.0/24).",
      );
      return;
    }

    const name = (newSubnetForm.name || "").trim();
    if (!name) {
      setSubnetFormError("Subnet Name is required.");
      return;
    }

    const kafka = (newSubnetForm.kafka_broker || "").trim();
    if (!kafka) {
      setSubnetFormError("Central Kafka Broker address is required.");
      return;
    }

    if (!newSubnetForm.authorized) {
      setSubnetFormError(
        "You must authorize package installation on the relay VM to proceed.",
      );
      return;
    }

    setIsInstallingRelay(true);
    setInstallLogs([]);

    try {
      const registeredSubnet = await addSubnet({
        subnet: subnet,
        name: name,
        floor: (newSubnetForm.floor || "").trim(),
        snmp_community: (newSubnetForm.snmp_community || "public").trim(),
        snmp_port: parseInt(newSubnetForm.snmp_port || 161, 10),
        status: "Active",
        relay_ip: (newSubnetForm.relay_ip || "").trim(),
        ssh_port: newSubnetForm.ssh_port,
        ssh_user: (newSubnetForm.ssh_user || "").trim(),
        ssh_pkey: (newSubnetForm.ssh_pkey || "").trim(),
        scan_interval_secs: newSubnetForm.scan_interval_secs,
        kafka_broker: kafka,
      });

      if (!registeredSubnet || !registeredSubnet.id) {
        throw new Error("Failed to register subnet in database");
      }

      if (onSuccess) onSuccess();

      setInstallLogs(["Initiating bootstrap connection..."]);
      const stream = bootstrapSubnet(registeredSubnet.id);

      stream.on("log", (line) => {
        setInstallLogs((prev) => [...prev, line]);
      });

      stream.on("status", (status) => {
        if (status === "completed") {
          setBootstrapSuccess(true);
          setInstallLogs((prev) => [
            ...prev,
            "Relay VM bootstrapped and configured successfully!",
          ]);
          if (onSuccess) onSuccess();
        } else if (status === "failed") {
          setBootstrapFailed(true);
          setInstallLogs((prev) => [
            ...prev,
            "❌ Bootstrap installation failed. Please review the logs above.",
          ]);
          if (onSuccess) onSuccess();
        }
      });

      stream.on("error", (err) => {
        setInstallLogs((prev) => [...prev, `❌ Stream error: ${err}`]);
        setBootstrapFailed(true);
        if (onSuccess) onSuccess();
      });
    } catch (err) {
      console.error(err);
      setSubnetFormError(
        err.message ||
          "Failed to register subnet. Please check connection settings.",
      );
      setIsInstallingRelay(false);
    }
  };

  return (
    <Modal
      title={
        isInstallingRelay
          ? "Relay Setup Progress"
          : "Add Subnet Relay Wizard"
      }
      onClose={handleClose}
      width={isInstallingRelay ? 520 : 480}
    >
      {isInstallingRelay ? (
        <div style={{ display: "flex", flexDirection: "column", gap: 14 }}>
          <div style={{ textAlign: "center", padding: "16px 0 10px" }}>
            {bootstrapFailed ? (
              <div
                style={{
                  display: "inline-flex",
                  alignItems: "center",
                  justifyContent: "center",
                  width: 36,
                  height: 36,
                  borderRadius: "50%",
                  background: "#FEF2F2",
                  border: "2px solid #FCA5A5",
                  marginBottom: 12,
                }}
              >
                <Icon name="alert" size={20} color="#DC2626" />
              </div>
            ) : bootstrapSuccess ? (
              <div
                style={{
                  display: "inline-flex",
                  alignItems: "center",
                  justifyContent: "center",
                  width: 36,
                  height: 36,
                  borderRadius: "50%",
                  background: "#ECFDF5",
                  border: "2px solid #A7F3D0",
                  marginBottom: 12,
                }}
              >
                <Icon name="check" size={20} color="#059669" />
              </div>
            ) : (
              <div
                style={{
                  display: "inline-block",
                  width: 32,
                  height: 32,
                  borderRadius: "50%",
                  border: "3px solid #E2E8F0",
                  borderTopColor: "#1D4ED8",
                  animation: "shimmer 1.2s linear infinite",
                  marginBottom: 12,
                }}
              />
            )}
            <h4
              style={{
                margin: 0,
                fontSize: 14,
                color: "#111827",
                fontWeight: 700,
              }}
            >
              {bootstrapFailed
                ? "Relay Bootstrap Failed"
                : bootstrapSuccess
                  ? "Relay Bootstrapped Successfully"
                  : "Agentless Relay Bootstrap"}
            </h4>
            <p
              style={{ margin: "4px 0 0", fontSize: 12, color: "#6B7280" }}
            >
              {bootstrapFailed
                ? "The installation has stopped. Review the error details below."
                : bootstrapSuccess
                  ? "The subnet relay has been successfully configured and activated."
                  : "Please wait while the platform installs discovery relays via SSH."}
            </p>
          </div>
          <div
            style={{
              background: "#0F172A",
              borderRadius: 8,
              padding: 14,
              fontFamily: "monospace",
              fontSize: 11.5,
              color: "#34D399",
              height: 180,
              overflowY: "auto",
              display: "flex",
              flexDirection: "column",
              gap: 6,
              border: "1px solid #1E293B",
              lineHeight: 1.5,
            }}
          >
            {installLogs.map((log, idx) => (
              <div
                key={idx}
                style={{
                  color:
                    log.startsWith("✓") || log.startsWith("✅")
                      ? "#34D399"
                      : log.startsWith("❌")
                        ? "#F87171"
                        : "#E2E8F0",
                }}
              >
                {log}
              </div>
            ))}
          </div>
          {(bootstrapFailed || bootstrapSuccess) && (
            <div
              style={{
                display: "flex",
                justifyContent: "flex-end",
                marginTop: 4,
              }}
            >
              <button
                onClick={handleClose}
                style={{
                  padding: "8px 16px",
                  background: bootstrapSuccess ? "#059669" : "#EF4444",
                  color: "#fff",
                  border: "none",
                  borderRadius: 6,
                  fontSize: 13,
                  fontWeight: 600,
                  cursor: "pointer",
                }}
              >
                {bootstrapSuccess ? "Done" : "Close"}
              </button>
            </div>
          )}
        </div>
      ) : (
        <div style={{ display: "flex", flexDirection: "column", gap: 14 }}>
          {/* Wizard Tabs Indicator */}
          <div
            style={{
              display: "flex",
              gap: 10,
              borderBottom: "1px solid #F3F4F6",
              paddingBottom: 12,
              marginBottom: 4,
            }}
          >
            <div
              style={{
                flex: 1,
                paddingBottom: 4,
                borderBottom: `2px solid ${wizardStep === 1 ? "#1D4ED8" : "transparent"}`,
                color: wizardStep === 1 ? "#1D4ED8" : "#9CA3AF",
                fontSize: 11,
                fontWeight: 700,
                textTransform: "uppercase",
                letterSpacing: "0.05em",
              }}
            >
              1. Relay Credentials
            </div>
            <div
              style={{
                flex: 1,
                paddingBottom: 4,
                borderBottom: `2px solid ${wizardStep === 2 ? "#1D4ED8" : "transparent"}`,
                color: wizardStep === 2 ? "#1D4ED8" : "#9CA3AF",
                fontSize: 11,
                fontWeight: 700,
                textTransform: "uppercase",
                letterSpacing: "0.05em",
              }}
            >
              2. Subnet Details
            </div>
          </div>

          {wizardStep === 1 ? (
            <>
              <div style={{ display: "flex", gap: 12 }}>
                <div
                  style={{
                    display: "flex",
                    flexDirection: "column",
                    gap: 4,
                    flex: 2,
                  }}
                >
                  <label
                    style={{
                      fontSize: 11.5,
                      fontWeight: 600,
                      color: "#374151",
                    }}
                  >
                    Relay VM IP Address *
                  </label>
                  <input
                    placeholder="e.g. 10.30.5.5"
                    disabled={isTestingSSH}
                    value={newSubnetForm.relay_ip}
                    onChange={(e) =>
                      setNewSubnetForm({
                        ...newSubnetForm,
                        relay_ip: e.target.value,
                      })
                    }
                    style={{
                      padding: "8px 10px",
                      border: "1px solid #D1D5DB",
                      borderRadius: 6,
                      fontSize: 13,
                      background: isTestingSSH ? "#F1F5F9" : "#fff",
                    }}
                  />
                </div>
                <div
                  style={{
                    display: "flex",
                    flexDirection: "column",
                    gap: 4,
                    flex: 1,
                  }}
                >
                  <label
                    style={{
                      fontSize: 11.5,
                      fontWeight: 600,
                      color: "#374151",
                    }}
                  >
                    SSH Port *
                  </label>
                  <input
                    type="text"
                    placeholder="22"
                    disabled={isTestingSSH}
                    value={newSubnetForm.ssh_port}
                    onChange={(e) => {
                      const val = e.target.value.replace(/\D/g, "");
                      setNewSubnetForm({
                        ...newSubnetForm,
                        ssh_port: val === "" ? "" : parseInt(val, 10),
                      });
                    }}
                    style={{
                      padding: "8px 10px",
                      border: "1px solid #D1D5DB",
                      borderRadius: 6,
                      fontSize: 13,
                      background: isTestingSSH ? "#F1F5F9" : "#fff",
                    }}
                  />
                </div>
              </div>

              <div
                style={{ display: "flex", flexDirection: "column", gap: 4 }}
              >
                <label
                  style={{
                    fontSize: 11.5,
                    fontWeight: 600,
                    color: "#374151",
                  }}
                >
                  SSH Username *
                </label>
                <input
                  placeholder="e.g. ubuntu"
                  disabled={isTestingSSH}
                  value={newSubnetForm.ssh_user}
                  onChange={(e) =>
                    setNewSubnetForm({
                      ...newSubnetForm,
                      ssh_user: e.target.value,
                    })
                  }
                  style={{
                    padding: "8px 10px",
                    border: "1px solid #D1D5DB",
                    borderRadius: 6,
                    fontSize: 13,
                    background: isTestingSSH ? "#F1F5F9" : "#fff",
                  }}
                />
              </div>

              <div
                style={{ display: "flex", flexDirection: "column", gap: 4 }}
              >
                <div
                  style={{
                    display: "flex",
                    justifyContent: "space-between",
                    alignItems: "center",
                  }}
                >
                  <label
                    style={{
                      fontSize: 11.5,
                      fontWeight: 600,
                      color: "#374151",
                    }}
                  >
                    SSH Private Key (PEM format) *
                  </label>
                  <button
                    type="button"
                    onClick={() => setShowRelayKey(!showRelayKey)}
                    disabled={isTestingSSH}
                    style={{
                      background: "none",
                      border: "none",
                      color: "#1D4ED8",
                      fontSize: 11,
                      fontWeight: 600,
                      cursor: "pointer",
                      display: "inline-flex",
                      alignItems: "center",
                      gap: 4,
                      padding: 0,
                    }}
                  >
                    <Icon
                      name={showRelayKey ? "eye-off" : "eye"}
                      size={13}
                      color="#1D4ED8"
                    />
                    {showRelayKey ? "Hide Key" : "Show Key"}
                  </button>
                </div>
                <textarea
                  placeholder="-----BEGIN RSA PRIVATE KEY-----&#10;..."
                  rows={5}
                  disabled={isTestingSSH}
                  value={newSubnetForm.ssh_pkey}
                  onChange={(e) =>
                    setNewSubnetForm({
                      ...newSubnetForm,
                      ssh_pkey: e.target.value,
                    })
                  }
                  style={{
                    padding: "8px 10px",
                    border: "1px solid #D1D5DB",
                    borderRadius: 6,
                    fontSize: 13,
                    fontFamily: "monospace",
                    background: isTestingSSH ? "#F1F5F9" : "#fff",
                    WebkitTextSecurity: showRelayKey ? "none" : "disc",
                    textSecurity: showRelayKey ? "none" : "disc",
                  }}
                />
              </div>

              {sshTestError && (
                <div
                  style={{
                    background: "#FEF2F2",
                    border: "1px solid #FEE2E2",
                    borderRadius: 8,
                    padding: "10px 12px",
                    display: "flex",
                    gap: 8,
                    alignItems: "flex-start",
                    marginTop: 4,
                  }}
                >
                  <Icon
                    name="alert"
                    size={16}
                    color="#DC2626"
                    style={{ marginTop: 2, flexShrink: 0 }}
                  />
                  <div
                    style={{
                      fontSize: 12,
                      color: "#991B1B",
                      lineHeight: 1.4,
                    }}
                  >
                    {sshTestError}
                  </div>
                </div>
              )}

              {sshTestSuccess && (
                <div
                  style={{
                    background: "#ECFDF5",
                    border: "1px solid #A7F3D0",
                    borderRadius: 8,
                    padding: "10px 12px",
                    display: "flex",
                    gap: 8,
                    alignItems: "center",
                    marginTop: 4,
                  }}
                >
                  <div
                    style={{
                      width: 12,
                      height: 12,
                      borderRadius: "50%",
                      border: "2px solid rgba(16,185,129,0.2)",
                      borderTopColor: "#10B981",
                      animation: "shimmer 0.8s linear infinite",
                      flexShrink: 0,
                    }}
                  />
                  <div
                    style={{
                      fontSize: 12,
                      color: "#065F46",
                      fontWeight: 600,
                    }}
                  >
                    ✓ SSH Connection Successful! Loading subnet details...
                  </div>
                </div>
              )}

              <div
                style={{
                  display: "flex",
                  justifyContent: "flex-end",
                  gap: 10,
                  borderTop: "1px solid #F3F4F6",
                  paddingTop: 14,
                }}
              >
                <button
                  onClick={handleClose}
                  disabled={isTestingSSH}
                  style={{
                    padding: "8px 16px",
                    background: "#fff",
                    border: "1px solid #D1D5DB",
                    borderRadius: 6,
                    fontSize: 13,
                    cursor: isTestingSSH ? "not-allowed" : "pointer",
                    opacity: isTestingSSH ? 0.6 : 1,
                  }}
                >
                  Cancel
                </button>
                <button
                  onClick={handleTestRelaySSH}
                  disabled={isTestingSSH}
                  style={{
                    padding: "8px 16px",
                    background: isTestingSSH ? "#BFDBFE" : "#1D4ED8",
                    color: "#fff",
                    border: "none",
                    borderRadius: 6,
                    fontSize: 13,
                    fontWeight: 600,
                    cursor: isTestingSSH ? "not-allowed" : "pointer",
                    display: "inline-flex",
                    alignItems: "center",
                    gap: 6,
                  }}
                >
                  {isTestingSSH && (
                    <div
                      style={{
                        width: 12,
                        height: 12,
                        borderRadius: "50%",
                        border: "2px solid rgba(255,255,255,0.2)",
                        borderTopColor: "#fff",
                        animation: "shimmer 0.8s linear infinite",
                      }}
                    />
                  )}
                  {isTestingSSH
                    ? "Testing Connection..."
                    : "Test Connection & Next"}
                </button>
              </div>
            </>
          ) : (
            <>
              <div style={{ display: "flex", gap: 12 }}>
                <div
                  style={{
                    display: "flex",
                    flexDirection: "column",
                    gap: 4,
                    flex: 1,
                  }}
                >
                  <label
                    style={{
                      fontSize: 11.5,
                      fontWeight: 600,
                      color: "#374151",
                    }}
                  >
                    Subnet CIDR *
                  </label>
                  <input
                    placeholder="e.g. 10.30.5.0/24"
                    value={newSubnetForm.subnet}
                    onChange={(e) =>
                      setNewSubnetForm({
                        ...newSubnetForm,
                        subnet: e.target.value,
                      })
                    }
                    style={{
                      padding: "8px 10px",
                      border: "1px solid #D1D5DB",
                      borderRadius: 6,
                      fontSize: 13,
                    }}
                  />
                </div>
                <div
                  style={{
                    display: "flex",
                    flexDirection: "column",
                    gap: 4,
                    flex: 1,
                  }}
                >
                  <label
                    style={{
                      fontSize: 11.5,
                      fontWeight: 600,
                      color: "#374151",
                    }}
                  >
                    Subnet Name *
                  </label>
                  <input
                    placeholder="e.g. Branch Office"
                    value={newSubnetForm.name}
                    onChange={(e) =>
                      setNewSubnetForm({
                        ...newSubnetForm,
                        name: e.target.value,
                      })
                    }
                    style={{
                      padding: "8px 10px",
                      border: "1px solid #D1D5DB",
                      borderRadius: 6,
                      fontSize: 13,
                    }}
                  />
                </div>
              </div>

              <div style={{ display: "flex", gap: 12 }}>
                <div
                  style={{
                    display: "flex",
                    flexDirection: "column",
                    gap: 4,
                    flex: 1,
                  }}
                >
                  <label
                    style={{
                      fontSize: 11.5,
                      fontWeight: 600,
                      color: "#374151",
                    }}
                  >
                    Location / Floor (Optional)
                  </label>
                  <input
                    placeholder="e.g. Floor 1 or GCP VPC"
                    value={newSubnetForm.floor}
                    onChange={(e) =>
                      setNewSubnetForm({
                        ...newSubnetForm,
                        floor: e.target.value,
                      })
                    }
                    style={{
                      padding: "8px 10px",
                      border: "1px solid #D1D5DB",
                      borderRadius: 6,
                      fontSize: 13,
                    }}
                  />
                </div>

                <div
                  style={{
                    display: "flex",
                    flexDirection: "column",
                    gap: 4,
                    flex: 1,
                  }}
                >
                  <label
                    style={{
                      fontSize: 11.5,
                      fontWeight: 600,
                      color: "#374151",
                    }}
                  >
                    SNMP Community
                  </label>
                  <input
                    placeholder="public"
                    value={newSubnetForm.snmp_community}
                    onChange={(e) =>
                      setNewSubnetForm({
                        ...newSubnetForm,
                        snmp_community: e.target.value,
                      })
                    }
                    style={{
                      padding: "8px 10px",
                      border: "1px solid #D1D5DB",
                      borderRadius: 6,
                      fontSize: 13,
                    }}
                  />
                </div>

                <div
                  style={{
                    display: "flex",
                    flexDirection: "column",
                    gap: 4,
                    width: 110,
                  }}
                >
                  <label
                    style={{
                      fontSize: 11.5,
                      fontWeight: 600,
                      color: "#374151",
                    }}
                  >
                    SNMP Port
                  </label>
                  <input
                    type="number"
                    placeholder="161"
                    value={newSubnetForm.snmp_port}
                    onChange={(e) =>
                      setNewSubnetForm({
                        ...newSubnetForm,
                        snmp_port: parseInt(e.target.value, 10) || 161,
                      })
                    }
                    style={{
                      padding: "8px 10px",
                      border: "1px solid #D1D5DB",
                      borderRadius: 6,
                      fontSize: 13,
                    }}
                  />
                </div>
              </div>

              <div
                style={{ display: "flex", flexDirection: "column", gap: 4 }}
              >
                <label
                  style={{
                    fontSize: 11.5,
                    fontWeight: 600,
                    color: "#374151",
                  }}
                >
                  Central Kafka Broker *
                </label>
                <input
                  value={newSubnetForm.kafka_broker}
                  onChange={(e) =>
                    setNewSubnetForm({
                      ...newSubnetForm,
                      kafka_broker: e.target.value,
                    })
                  }
                  style={{
                    padding: "8px 10px",
                    border: "1px solid #D1D5DB",
                    borderRadius: 6,
                    fontSize: 13,
                  }}
                />
              </div>

              <label
                style={{
                  display: "flex",
                  alignItems: "flex-start",
                  gap: 8,
                  fontSize: 12.5,
                  color: "#374151",
                  cursor: "pointer",
                  background: "#F9FAFB",
                  padding: 10,
                  border: "1px solid #E5E7EB",
                  borderRadius: 6,
                  marginTop: 4,
                }}
              >
                <input
                  type="checkbox"
                  checked={newSubnetForm.authorized}
                  onChange={(e) =>
                    setNewSubnetForm({
                      ...newSubnetForm,
                      authorized: e.target.checked,
                    })
                  }
                  style={{ marginTop: 3 }}
                />
                <span>
                  I confirm authorization to seed the relay VM and trigger
                  network port sweeps.
                </span>
              </label>

              {subnetFormError && (
                <div
                  style={{
                    background: "#FEF2F2",
                    border: "1px solid #FEE2E2",
                    borderRadius: 8,
                    padding: "10px 12px",
                    display: "flex",
                    gap: 8,
                    alignItems: "flex-start",
                    marginTop: 4,
                  }}
                >
                  <Icon
                    name="alert"
                    size={16}
                    color="#DC2626"
                    style={{ marginTop: 2, flexShrink: 0 }}
                  />
                  <div
                    style={{
                      fontSize: 12,
                      color: "#991B1B",
                      lineHeight: 1.4,
                    }}
                  >
                    {subnetFormError}
                  </div>
                </div>
              )}

              <div
                style={{
                  display: "flex",
                  justifyContent: "space-between",
                  borderTop: "1px solid #F3F4F6",
                  paddingTop: 14,
                }}
              >
                <button
                  onClick={() => {
                    setWizardStep(1);
                    setSshTestError(null);
                    setSubnetFormError(null);
                  }}
                  style={{
                    padding: "8px 16px",
                    background: "#fff",
                    border: "1px solid #D1D5DB",
                    borderRadius: 6,
                    fontSize: 13,
                    cursor: "pointer",
                  }}
                >
                  Back
                </button>
                <div style={{ display: "flex", gap: 10 }}>
                  <button
                    onClick={handleClose}
                    style={{
                      padding: "8px 16px",
                      background: "#fff",
                      border: "1px solid #D1D5DB",
                      borderRadius: 6,
                      fontSize: 13,
                      cursor: "pointer",
                    }}
                  >
                    Cancel
                  </button>
                  <button
                    onClick={handleSaveSubnet}
                    style={{
                      padding: "8px 16px",
                      background: "#1D4ED8",
                      color: "#fff",
                      border: "none",
                      borderRadius: 6,
                      fontSize: 13,
                      fontWeight: 600,
                      cursor: "pointer",
                    }}
                  >
                    Bootstrap & Save
                  </button>
                </div>
              </div>
            </>
          )}
        </div>
      )}
    </Modal>
  );
}

export default SubnetRelayWizardModal;
